import type { ContractKind } from "@/services/docuseal";
import type { StakeholderPickerParty } from "@/components/common/StakeholderPicker";
import type { ConfigField, Stakeholders } from "../types";

export type TemplateOption = {
  id: number;
  name: string;
  jurisdiction: string | null;
  is_default: boolean;
  /** A system template every org shares. */
  is_system: boolean;
  /** A custom upload with nothing for the merchant to sign or fill in. */
  document_only?: boolean;
};

export type JurisdictionOption = { code: string | null; name: string };

export function templateOptions(field: ConfigField): TemplateOption[] {
  return Array.isArray(field.options) ? (field.options as TemplateOption[]) : [];
}

export function jurisdictionOptions(field: ConfigField): JurisdictionOption[] {
  return Array.isArray(field.options) ? (field.options as JurisdictionOption[]) : [];
}

export function termsKind(field: ConfigField): ContractKind | null {
  const opts = field.options;
  if (opts && typeof opts === "object" && "kind" in opts) {
    const kind = (opts as { kind?: unknown }).kind;
    if (
      kind === "main_contract" ||
      kind === "refinance_contract" ||
      kind === "disclosure" ||
      kind === "addendum" ||
      kind === "ach_addendum"
    ) {
      return kind;
    }
  }
  return null;
}

export const STAKEHOLDER_KEYS = ["merchant", "broker", "me", "accounting", "thread"] as const;

export const STAKEHOLDER_LABELS: Record<(typeof STAKEHOLDER_KEYS)[number], string> = {
  merchant: "Merchant",
  broker: "Broker",
  me: "Me",
  accounting: "Accounting",
  thread: "Email thread",
};

function isParty(value: unknown): value is StakeholderPickerParty {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Record<string, unknown>;
  return typeof row.key === "string" && typeof row.label === "string";
}

/** The server's parties (labels, locks) for a stakeholders field; the keys alone as a fallback. */
export function stakeholderParties(field: ConfigField): StakeholderPickerParty[] {
  const parties = Array.isArray(field.options) ? field.options.filter(isParty) : [];
  if (parties.length) return parties;
  return STAKEHOLDER_KEYS.map((key) => ({ key, label: STAKEHOLDER_LABELS[key] }));
}

export function stakeholdersValue(value: unknown): Stakeholders {
  const row = value && typeof value === "object" ? (value as Partial<Stakeholders>) : {};
  return Object.fromEntries(
    STAKEHOLDER_KEYS.map((key) => [key, Boolean(row[key])])
  ) as Stakeholders;
}

/** Radix Select cannot use `""`; the general disclosure lane is `code: null`. */
export const GENERAL_LANE = "__general__";

export function jurisdictionSelectValue(code: string | null | undefined): string {
  return code ?? GENERAL_LANE;
}

export function jurisdictionFromSelect(value: string): string | null {
  return value === GENERAL_LANE ? null : value;
}
